import { useMemo, useState } from 'react';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Clock,
  User as UserIcon,
  Copy,
  Check,
  Loader2,
  ShoppingCart,
  Ban,
  Undo2,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useAdminCarts,
  useAdminCart,
  useApproveCart,
  useRejectCart,
  useCancelCart,
  useUncancelCart,
  useDeleteCart,
  type AdminCart,
  type AdminCartItem,
} from '@/hooks/useCrypto';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useAlert, useConfirm } from '@/components/ConfirmDialog';

const EXPLORER: Record<string, (tx: string) => string> = {
  tron: (tx) => `https://tronscan.org/#/transaction/${tx}`,
  ethereum: (tx) => `https://etherscan.io/tx/${tx}`,
  bsc: (tx) => `https://bscscan.com/tx/${tx}`,
  polygon: (tx) => `https://polygonscan.com/tx/${tx}`,
  arbitrum: (tx) => `https://arbiscan.io/tx/${tx}`,
  optimism: (tx) => `https://optimistic.etherscan.io/tx/${tx}`,
  base: (tx) => `https://basescan.org/tx/${tx}`,
  avalanche: (tx) => `https://snowtrace.io/tx/${tx}`,
  solana: (tx) => `https://solscan.io/tx/${tx}`,
  ton: (tx) => `https://tonviewer.com/transaction/${tx}`,
};

function formatPrice(cents: number | null): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export function PlatformCartsPage() {
  const { data, isPending, isFetching } = useAdminCarts();

  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const carts = data?.carts ?? [];
  const filtered = filter === 'pending'
    ? carts.filter((c) => c.status === 'pending_verification' || c.status === 'pending')
    : carts;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Sepet Onay Kuyruğu
          </h1>
          <p className="text-muted-foreground text-sm">
            Çoklu-ürün kripto sepetleri. Tek onayla sepetteki tüm kalemler işlenir (bakiye yüklenir + eklentiler aktive edilir).
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => setFilter('pending')}
            className={cn(
              'rounded-md px-2 py-1 border',
              filter === 'pending' ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground',
            )}
          >
            Bekleyen
          </button>
          <button
            onClick={() => setFilter('all')}
            className={cn(
              'rounded-md px-2 py-1 border',
              filter === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground',
            )}
          >
            Hepsi
          </button>
          <span className="text-muted-foreground ml-2">
            {isFetching ? 'Yenileniyor…' : 'Her 30 sn yenilenir'}
          </span>
        </div>
      </div>

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <ShoppingCart className="w-6 h-6 mx-auto mb-2 opacity-50" />
            {filter === 'pending' ? 'Onay bekleyen sepet yok.' : 'Hiç sepet yok.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => <CartRow key={c.id} cart={c} />)}
        </div>
      )}
    </div>
  );
}

function CartRow({ cart }: { cart: AdminCart }) {
  const [expanded, setExpanded] = useState(cart.status === 'pending_verification');
  const detail = useAdminCart(expanded ? cart.id : undefined);
  const approve = useApproveCart();
  const reject = useRejectCart();
  const cancel = useCancelCart();
  const uncancel = useUncancelCart();
  const remove = useDeleteCart();
  const confirm = useConfirm();
  const alertDialog = useAlert();
  const [note, setNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [copied, setCopied] = useState(false);

  const hasTxid = !!cart.crypto_txid;
  const isPending = cart.status === 'pending';
  const isAwaiting = cart.status === 'pending_verification';
  const isPaid = cart.status === 'paid';
  const isFailed = cart.status === 'failed';
  const isCancelled = cart.status === 'cancelled';
  const canCancel = isPending || isAwaiting;

  const explorerUrl = cart.crypto_txid && cart.crypto_network
    ? EXPLORER[cart.crypto_network]?.(cart.crypto_txid)
    : null;

  const grouped = useMemo(() => {
    const items: AdminCartItem[] = detail.data?.items ?? [];
    const map = new Map<string, { name: string; qty: number; unit_cents: number; kind: string | null; duration_days: number | null }>();
    for (const it of items) {
      const row = map.get(it.package_id) ?? {
        name: it.package_name ?? it.package_id,
        qty: 0,
        unit_cents: it.amount_paid ?? 0,
        kind: it.kind,
        duration_days: it.duration_days,
      };
      row.qty++;
      map.set(it.package_id, row);
    }
    return Array.from(map.values());
  }, [detail.data]);

  const onCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onApprove = async () => {
    const ok = await confirm({
      title: 'Sepeti onayla?',
      description: `${cart.org_name ?? cart.user_email} için ${cart.item_count} kalem işlenecek (toplam ${formatPrice(cart.total_amount_usd_cents)}).`,
      confirmText: 'Onayla ve işle',
    });
    if (!ok) return;
    await approve.mutateAsync({ id: cart.id, note: note || undefined });
    setNote('');
  };

  const onReject = async () => {
    if (!rejectReason.trim()) {
      await alertDialog({
        title: 'Red nedeni zorunlu',
        description: 'Kullanıcıya geri bildirim için kısa bir not gir.',
        danger: true,
      });
      return;
    }
    const ok = await confirm({
      title: 'Sepeti reddet?',
      description: 'Tüm kalemler failed olarak işaretlenir. Bakiye yüklenmez, eklenti açılmaz.',
      confirmText: 'Reddet',
      danger: true,
    });
    if (!ok) return;
    await reject.mutateAsync({ id: cart.id, reason: rejectReason });
    setRejectReason('');
  };

  return (
    <Card className={cn(isAwaiting && 'ring-1 ring-primary')}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold flex items-center gap-1.5">
                <ShoppingCart className="w-4 h-4" />
                {cart.item_count} kalem
              </span>
              <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5 font-mono">
                {cart.id}
              </span>
              {isAwaiting && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs px-2 py-0.5">
                  <Clock className="w-3 h-3" /> TXID geldi
                </span>
              )}
              {isPending && (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5">
                  TXID bekleniyor
                </span>
              )}
              {isPaid && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-xs px-2 py-0.5">
                  <CheckCircle2 className="w-3 h-3" /> Onaylı
                  {cart.verify_auto ? ' · auto' : ''}
                </span>
              )}
              {isAwaiting && (cart.verify_attempts ?? 0) > 0 && (
                <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5 font-mono">
                  auto: {cart.verify_attempts} deneme
                </span>
              )}
              {isFailed && (
                <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 text-destructive text-xs px-2 py-0.5">
                  <XCircle className="w-3 h-3" /> Reddedildi
                </span>
              )}
              {isCancelled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5">
                  <Ban className="w-3 h-3" /> İptal edildi
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground space-x-2">
              <span className="inline-flex items-center gap-1">
                <UserIcon className="w-3 h-3" />
                {cart.user_email}
              </span>
              <span>· {cart.org_name}</span>
              <span>· Oluşturuldu: {formatDate(cart.created_at)}</span>
              {cart.crypto_txid_submitted_at && (
                <span>· TXID: {formatDate(cart.crypto_txid_submitted_at)}</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold font-mono">
              {cart.crypto_expected_amount} {cart.crypto_asset}
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {formatPrice(cart.total_amount_usd_cents)}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-primary hover:underline"
        >
          {expanded ? 'Kalemleri gizle' : 'Kalemleri göster'}
        </button>

        {expanded && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            {detail.isPending ? (
              <div className="text-xs text-muted-foreground">Yükleniyor…</div>
            ) : grouped.length === 0 ? (
              <div className="text-xs text-muted-foreground">Kalem yok.</div>
            ) : (
              <ul className="space-y-1 text-sm">
                {grouped.map((row, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {row.name}
                        {row.qty > 1 && <span className="text-muted-foreground"> × {row.qty}</span>}
                      </div>
                      {row.duration_days && row.kind !== 'balance' && (
                        <div className="text-xs text-muted-foreground">{row.duration_days} gün</div>
                      )}
                    </div>
                    <div className="font-mono text-sm shrink-0">
                      {formatPrice(row.unit_cents * row.qty)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {cart.crypto_address && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            <div className="text-xs text-muted-foreground">Ödeme adresi ({cart.crypto_network}):</div>
            <div className="flex gap-2 items-center">
              <code className="flex-1 min-w-0 font-mono text-xs break-all">{cart.crypto_address}</code>
              <Button size="sm" variant="outline" onClick={() => onCopy(cart.crypto_address!)}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        )}

        {hasTxid && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-primary">Kullanıcının Gönderdiği TXID</div>
              {explorerUrl && (
                <a
                  href={explorerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Explorer <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <code className="block font-mono text-xs break-all">{cart.crypto_txid}</code>
          </div>
        )}

        {isFailed && cart.verification_notes && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs">
            <div className="font-medium text-destructive mb-0.5">Red nedeni</div>
            <div className="text-muted-foreground">{cart.verification_notes}</div>
          </div>
        )}

        {isAwaiting && (
          <div className="grid gap-3 md:grid-cols-2 pt-2 border-t">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Onay notu (opsiyonel)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              />
              <Button
                onClick={onApprove}
                disabled={approve.isPending}
                className="w-full"
                size="sm"
              >
                {approve.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Onayla & Sepeti İşle
              </Button>
            </div>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Red nedeni (zorunlu)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              />
              <Button
                onClick={onReject}
                disabled={reject.isPending || !rejectReason.trim()}
                className="w-full"
                size="sm"
                variant="destructive"
              >
                {reject.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Reddet
              </Button>
            </div>
          </div>
        )}

        {isPending && (
          <div className="text-xs text-muted-foreground">
            Kullanıcı henüz TXID göndermedi. 24 saat içinde gelmezse siparişi manuel iptal edebilirsin.
          </div>
        )}

        {(canCancel || isCancelled) && (
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
            {canCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Sepeti iptal et?',
                    description:
                      'Kullanıcıya "ödeme reddedildi" olarak gözükür. Bu adım geri alınabilir (Kuyruğa Geri Al).',
                    confirmText: 'İptal Et',
                    danger: true,
                  });
                  if (!ok) return;
                  try {
                    await cancel.mutateAsync({ id: cart.id });
                  } catch (e) {
                    await alertDialog({
                      title: 'İptal edilemedi',
                      description: e instanceof Error ? e.message : 'Beklenmedik hata',
                      danger: true,
                    });
                  }
                }}
                disabled={cancel.isPending}
              >
                <Ban className="w-3.5 h-3.5" /> İptal Et
              </Button>
            )}
            {isCancelled && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await uncancel.mutateAsync(cart.id);
                    } catch (e) {
                      await alertDialog({
                        title: 'Geri alınamadı',
                        description: e instanceof Error ? e.message : 'Beklenmedik hata',
                        danger: true,
                      });
                    }
                  }}
                  disabled={uncancel.isPending}
                >
                  <Undo2 className="w-3.5 h-3.5" /> Kuyruğa Geri Al
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Kalıcı sil?',
                      description:
                        'Sepet ve tüm kalemler veritabanından silinir. Geri alınamaz.',
                      confirmText: 'Kalıcı Sil',
                      danger: true,
                    });
                    if (!ok) return;
                    try {
                      await remove.mutateAsync(cart.id);
                    } catch (e) {
                      await alertDialog({
                        title: 'Silinemedi',
                        description: e instanceof Error ? e.message : 'Beklenmedik hata',
                        danger: true,
                      });
                    }
                  }}
                  disabled={remove.isPending}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Kalıcı Sil
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
